import React, { useEffect, useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import CloseIcon from '@mui/icons-material/Close'
import CircularProgress from '@mui/material/CircularProgress'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { toast } from 'react-toastify'
import DOMPurify from 'dompurify'
import api from '../../api/api'
import { useAuth } from '../../features/auth/AuthContext'
import useImagesPreloaded from '../../hooks/useImagesPreloaded'
import './LowerSchoolAnalyticsModal.css'
// import { ChevronLeft, ChevronRight } from '../FileRepo/Icons'
// import { ChevronLeft, ChevronRight } from '@mui/icons-material'


/**
 * Teachers read a named student's result; a student reads their own, and the
 * backend takes them from the token — so no id goes on the URL.
 */
const buildAnalyticsUrl = (lessonId, studentId) =>
  studentId
    ? `/lessons/${lessonId}/lower-school/result/${studentId}/`
    : `/lessons/${lessonId}/lower-school/result/`

const LowerSchoolAnalyticsModal = ({
  open = false,
  onClose,
  lessonId,
  student,
  /** Lesson name for the head, when the caller already has it — otherwise it
   *  comes from the analytics payload. */
  heading = '',
  /**
   * The student reading their own result: the modal opens on the completion
   * card, skips the key learnings / areas for improvement summary, and the
   * feedback panel is titled for them. Teachers open on that summary.
   */
  forStudent = false,
  /** `completion_html` from the socket's `completed` frame, when the caller has
   *  it; otherwise it is taken from the analytics payload. */
  completionHtml = '',
}) => {
  const { token } = useAuth()

  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(false)
  /** Index into `pages` — completion (student) or summary (teacher), then one per screen. */
  const [pageIndex, setPageIndex] = useState(0)

  const studentId = student?.student_id

  useEffect(() => {
    // No studentId is the student's own result, not a missing one.
    if (!open || !lessonId || !token) return

    let isCancelled = false

    const getAnalytics = async () => {
      setLoading(true)
      try {
        const response = await api.get(buildAnalyticsUrl(lessonId, studentId), {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
        })
        if (isCancelled) return
        if (response.status === 200) {
          setAnalytics(response.data?.data ?? null)
        } else {
          toast.error('Something went wrong.', { position: 'top-center' })
        }
      } catch (error) {
        if (isCancelled) return
        toast.error(
          error.response?.data?.message || 'Failed to load the analytics.',
          { position: 'top-center' },
        )
      } finally {
        if (!isCancelled) setLoading(false)
      }
    }

    getAnalytics()

    return () => {
      isCancelled = true
    }
  }, [open, lessonId, studentId, token])

  // Every open starts on the first page with fresh data.
  useEffect(() => {
    if (open) return
    setPageIndex(0)
    setAnalytics(null)
  }, [open])

  const keyLearnings = analytics?.summary?.key_learnings ?? []
  const areasForImprovement = analytics?.summary?.areas_for_improvement ?? []
  const screens = useMemo(() => analytics?.screens ?? [], [analytics])

  // completion_html is backend-authored markup — sanitize before injecting.
  const completionMarkup = completionHtml || analytics?.completion_html || ''
  const safeCompletionHtml = useMemo(
    () => (completionMarkup ? DOMPurify.sanitize(completionMarkup) : ''),
    [completionMarkup],
  )
  /** The markup ships its own CTA — only add ours when it has none. */
  const hasCompletionCta = /<button/i.test(safeCompletionHtml)

  /**
   * The pages in order: the student gets the completion card then one page per
   * screen; the teacher gets the summary — key learnings and areas for
   * improvement — then the screens. Built as a list so the indices stay right
   * whichever first page is in play.
   */
  const pages = useMemo(() => {
    const list = forStudent ? [{ type: 'completion' }] : [{ type: 'summary' }]
    screens.forEach((item, index) =>
      list.push({ type: 'screen', screen: item, number: index + 1 }),
    )
    return list
  }, [forStudent, screens])

  const totalPages = pages.length
  const page = pages[Math.min(pageIndex, totalPages - 1)]
  const isCompletionPage = page?.type === 'completion'
  const isSummaryPage = page?.type === 'summary'
  const screen = page?.screen ?? null

  // Hold the loader until this screen's option images are downloaded, so the
  // page never paints with empty image slots.
  const screenImageUrls = useMemo(
    () => (screen?.options ?? []).map((option) => option?.image_url).filter(Boolean),
    [screen],
  )
  const areImagesReady = useImagesPreloaded(screenImageUrls)

  const goBack = () => setPageIndex((index) => Math.max(0, index - 1))
  const goNext = () =>
    setPageIndex((index) => Math.min(totalPages - 1, index + 1))

  /**
   * The completion markup's own button moves on to the result — delegated,
   * since events bubble out of `dangerouslySetInnerHTML` content like any
   * other DOM.
   */
  const handleCompletionClick = (event) => {
    if (!event.target.closest('button')) return
    event.preventDefault()
    goNext()
  }

  return (
    <Modal open={open} onClose={onClose}>
      <div className={`ls-analytics ${isCompletionPage ? 'is-completion' : ''}`}>
        <div className="ls-analytics-head">
          <div>
            <h3>
              {heading ||
                analytics?.lesson?.name ||
                analytics?.lesson?.story_title ||
                'Lesson'}
            </h3>
            {/* the teacher needs to know whose result this is; the student
                reading their own already knows */}
            {/* {!forStudent && (
              <span>
                {student?.student_name ||
                  analytics?.student?.name ||
                  analytics?.lesson?.story_title ||
                  'Student'}
              </span>
            )} */}
          </div>
          <button
            type="button"
            className="ls-analytics-close"
            onClick={onClose}
            aria-label="Close analytics"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="ls-analytics-body">
          {/* markup handed in by the caller shows straight away — only the
              analytics-backed pages wait on the fetch, and a screen page also
              waits on its own images */}
          {(loading && !(isCompletionPage && safeCompletionHtml)) ||
          (screen && !areImagesReady) ? (
            <div className="ls-analytics-loader">
              <CircularProgress size={28} sx={{ color: '#0068ff' }} />
            </div>
          ) : isCompletionPage ? (
            <div className="ls-analytics-completion">
              {safeCompletionHtml ? (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                <div
                  className="ls-analytics-completion-html"
                  onClick={handleCompletionClick}
                  dangerouslySetInnerHTML={{ __html: safeCompletionHtml }}
                />
              ) : (
                // No markup came back — the card still needs to say what it says.
                <>
                  <h2>Great job on completing this lesson</h2>
                  {analytics?.lesson?.description && (
                    <p>{analytics.lesson.description}</p>
                  )}
                </>
              )}
              {!hasCompletionCta && (
                <button
                  type="button"
                  className="ls-analytics-completion-btn"
                  onClick={goNext}
                >
                  View Result
                </button>
              )}
            </div>
          ) : isSummaryPage ? (
            <div className="ls-analytics-summary">
              <section className="ls-analytics-panel ls-analytics-panel-learnings">
                <h4>Key Learnings</h4>
                <ul>
                  {keyLearnings.length ? (
                    keyLearnings.map((item, index) => (
                      <li key={item?.id ?? index}>{item?.learning} - {item?.note}</li>
                    ))
                  ) : (
                    <li className="ls-analytics-empty">
                      No key learnings recorded
                    </li>
                  )}
                </ul>
              </section>

              <section className="ls-analytics-panel bg-[#FFECD6]">
                <h4>Areas for Improvement</h4>
                <ul>
                  {areasForImprovement.length ? (
                    areasForImprovement.map((item, index) => (
                      <li key={item?.id ?? index}> {item?.learning} - {item?.note}</li>
                    ))
                  ) : (
                    <li className="ls-analytics-empty">
                      Nothing flagged for improvement
                    </li>
                  )}
                </ul>
              </section>
            </div>
          ) : (
            <div className="ls-analytics-screen">
              <div className="ls-analytics-screen-main">
                {(screen?.substory) && (
                  <div className="ls-analytics-substory">
                    {screen?.substory && <p>{screen.substory}</p>}
                    {/* {screen?.question && (
                      <p className="ls-analytics-question">{screen.question}</p>
                    )} */}
                  </div>
                )}

                <div className="ls-analytics-options">
                  {/* Options carry no id — `chosen` marks what the student picked. */}
                  {(screen?.options ?? []).map((option, index) => (
                    <div
                      key={`${option?.text ?? 'option'}-${index}`}
                      className={`ls-analytics-option `}
                    >
                      {option?.image_url && (
                        <img className={`${
                        option?.chosen ? 'is-chosen' : ''
                      }`} src={option.image_url} alt="" />
                      )}
                      <span className={`${
                        option?.chosen ? 'is-chosen' : ''
                      } ls-analytics-option-text`} >{option?.text}</span>
                    </div>
                  ))}
                </div>
                {screen && screens.length > 0 && (
              <div className="ls-analytics-pager">
                {/* <button
                  type="button"
                  onClick={goBack}
                  disabled={page?.number <= 1}
                  aria-label="Previous screen"
                >
                  <ChevronLeft sx={{ fontSize: 18 }} />
                </button> */}
                <span>
                  Your story path ({page?.number}/{screens.length})
                </span>
                {/* <button
                  type="button"
                  onClick={goNext}
                  disabled={pageIndex >= totalPages - 1}
                  aria-label="Next screen"
                >
                  <ChevronRight />
                </button> */}
              </div>
            )}
              </div>

              <aside className="ls-analytics-feedback">
                {forStudent ? (
                  // The student sees it as their feedback, with the AI credited
                  // off to the side rather than in the heading.
                  <div className="ls-analytics-feedback-head">
                    <h4>Feedback</h4>
                    <span>Generated with AI</span>
                  </div>
                ) : (
                  <h4>AI Feedback</h4>
                )}
                <p>{screen?.ai_feedback || 'No feedback for this screen.'}</p>
              </aside>
            </div>
          )}
        </div>

        {/* the completion card carries its own CTA — no footer behind it */}
        {!isCompletionPage && (
          <div className="ls-analytics-footer">
            <button
              type="button"
              className="ls-analytics-back"
              onClick={goBack}
              disabled={pageIndex <= 0}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 16 }} /> Back
            </button>

            {/* {screen && screens.length > 0 && (
              <div className="ls-analytics-pager">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={page?.number <= 1}
                  aria-label="Previous screen"
                >
                  <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                </button>
                <span>
                  Assessment ({page?.number}/{screens.length})
                </span>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={pageIndex >= totalPages - 1}
                  aria-label="Next screen"
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
            )} */}

            <button
              type="button"
              className="ls-analytics-next"
              onClick={goNext}
              disabled={pageIndex >= totalPages - 1}
            >
              Next <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default LowerSchoolAnalyticsModal
